import cx from 'classnames';
import { useRef, useState, useEffect } from 'react';
import { ReactComponent as WatchIcon } from '../assets/icons/star.svg';
import useFavoriteMutation from '../lib/useFavoriteMutation';
import Loader from './Loader';
import { useSaleTable } from './SaleTable';
import Table, { TableCell, TableRow } from './Table';

export default function SaleTableRows({ showWatch = true }) {
  const node = useRef();
  const [flashingRows, setFlashingRows] = useState([]);
  const prevRowsRef = useRef(null);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    loading,
    rows,
    prepareRow,
  } = useSaleTable();

  const [favorite, favoriteQuery] = useFavoriteMutation();

  useEffect(() => {
    if (!prevRowsRef.current) {
      prevRowsRef.current = rows.map(row => row.original);
      return;
    }

    const prevRows = prevRowsRef.current;
    const changedIds = [];

    rows.forEach(({ original }) => {
      const prev = prevRows.find(r => r.id === original.id);
      if (prev && JSON.stringify(prev) !== JSON.stringify(original)) {
        changedIds.push(original.id);
      }
    });

    prevRowsRef.current = rows.map(row => row.original);

    setFlashingRows(flashingRows => [...flashingRows, ...changedIds]);

    setTimeout(() => {
      setFlashingRows(flashingRows =>
        flashingRows.filter(id => !changedIds.includes(id)),
      );
    }, 1000);
  }, [rows]);

  return (
    <div className="tw-relative">
      <div className="tw-overflow-auto" ref={node}>
        <div>
          <Table {...getTableProps()} className="tw-overflow-hidden">
            <thead>
              {headerGroups.map(headerGroup => (
                <TableRow
                  header
                  {...headerGroup.getHeaderGroupProps()}
                  key={headerGroup.getHeaderGroupProps().key}
                  className="tw-h-14 touch:tw-h-16">
                  {headerGroup.headers.map(column => (
                    <TableCell
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.getHeaderProps().key}
                      className="tw-select-none tw-group tw-trasition-colors tw-duration-75 tw-ease"
                      header
                      title="Sort results by column">
                      <div className="tw-flex tw-items-center">
                        <div
                          className={
                            column.canSort ? 'group-hover:tw-text-black' : ''
                          }>
                          {column.render('Header')}
                        </div>
                        {column.canSort ? (
                          <i
                            className={cx(
                              'tw-ml-2',
                              column.isSorted ? '' : 'tw-invisible',
                              `gb-icon gb-icon-arrow-${
                                column.isSorted
                                  ? column.isSortedDesc
                                    ? 'down'
                                    : 'up'
                                  : column.sortDescFirst
                                  ? 'down'
                                  : 'up'
                              }`,
                            )}
                          />
                        ) : null}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <TableRow
                    {...row.getRowProps()}
                    key={row.id}
                    className="tw-h-14 touch:tw-h-16"
                    flash={flashingRows.includes(row.original.id)}
                    isSelected={row.isSelected}>
                    {row.cells.map((cell, index) => {
                      return (
                        <TableCell
                          {...cell.getCellProps()}
                          key={cell.getCellProps().key}>
                          {index === 0 ? (
                            <div className="tw-flex tw-h-14 touch:tw-h-16">
                              {showWatch && (
                                <button
                                  className="tw-inline-flex tw-items-center tw-mr-3 tw-text-gray-400 tw-self-center"
                                  disabled={favoriteQuery.loading}
                                  onClick={async () => {
                                    try {
                                      await favorite({
                                        [row.original.isFavorited
                                          ? 'remove'
                                          : 'add']: [row.original.id],
                                      });
                                    } catch (error) {
                                      window.alert(
                                        `Failed to ${
                                          row.original.isFavorited
                                            ? 'remove domain from favorites.'
                                            : 'add domain to favorites.'
                                        }`,
                                      );
                                      console.error('Favorite query error:');
                                      console.error(error);
                                    }
                                  }}
                                  title={
                                    row.original.isFavorited
                                      ? 'Remove domain from favorites'
                                      : 'Add domain to favorites'
                                  }>
                                  <WatchIcon
                                    className={cx(
                                      'tw-w-5 tw-h-5 hover:tw-text-orange',
                                      {
                                        'tw-text-orange tw-fill-current':
                                          row.original.isFavorited,
                                      },
                                    )}
                                  />
                                </button>
                              )}
                              <div className="tw-flex-auto tw-flex">
                                {cell.render('Cell')}
                              </div>
                            </div>
                          ) : (
                            cell.render('Cell')
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </div>
        {loading && (
          <Loader
            className={cx(
              'tw-py-24 tw-bg-white tw-bg-opacity-50 tw-pointer-events-none',
              {
                'tw-absolute tw-inset-0': rows.length > 0,
              },
            )}
            message={rows.length > 0 ? '' : undefined}
          />
        )}
      </div>
    </div>
  );
}
